import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Undo } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/utils/clipboard";
import { cn } from "@/lib/utils";

interface MessageControlProps {
  content: string;
  isStreaming?: boolean;
  onQuote?: (content: string) => void;
  className?: string;
}

/**
 * AI 消息操作栏组件
 * 提供复制和引用功能，仅在 AI 消息完成后显示
 */
export function MessageControl({
  content,
  isStreaming = false,
  onQuote,
  className,
}: MessageControlProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [showQuoteTooltip, setShowQuoteTooltip] = useState(false);

  // 处理复制功能
  const handleCopy = useCallback(async () => {
    if (isCopied || !content) return;

    const success = await copyToClipboard(content);

    // 操作成功的2秒延时
    if (success) {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } else {
      console.error("复制失败");
    }
  }, [content, isCopied]);

  // 处理引用功能
  const handleQuote = useCallback(() => {
    if (!content || !onQuote) return;
    onQuote(content);
  }, [content, onQuote]);

  if (isStreaming) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn("flex items-center gap-1 mt-1", className)}
    >
      {/* 复制按钮 */}
      <div className="relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCopy}
          onMouseEnter={() => setShowCopyTooltip(true)}
          onMouseLeave={() => setShowCopyTooltip(false)}
          className={cn(
            "h-7 w-7 transition-colors",
            isCopied
              ? "text-green-600 hover:text-green-600"
              : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"
          )}
          aria-label={isCopied ? "已复制" : "复制内容"}
        >
          {isCopied ? (
            <Check className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
        {showCopyTooltip && !isCopied && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap pointer-events-none z-50"
          >
            复制内容
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
          </motion.div>
        )}
      </div>

      {/* 引用按钮 */}
      {onQuote && (
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleQuote}
            onMouseEnter={() => setShowQuoteTooltip(true)}
            onMouseLeave={() => setShowQuoteTooltip(false)}
            className="h-7 w-7 text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label="回到此对话"
          >
            <Undo className="h-4 w-4" />
          </Button>
          {showQuoteTooltip && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1 }}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap pointer-events-none z-50"
            >
              回到此对话
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
            </motion.div>
          )}
        </div>
      )}
    </motion.div>
  );
}
